/**
 * Media Service - Streams MediaItems from database
 *
 * Purpose: Stream existing MediaItem records from the database endpoint
 * Does NOT handle Drive sync operations (use driveService for that)
 */

import { MediaItem } from "@/core/MediaItem/MediaItem.model";
import { API_CONFIG } from "@/config/api";
import { MediaStage, PostStatus } from "@/core/types/mediaTypes";
import { MediaContentType } from "@/core/types/mediaContentType";
import { UserRole } from '@/services/urlService';

const API_URL = API_CONFIG.URL;

// Helper to get access token
const getAccessToken = (): string | null => {
  const storedUser = localStorage.getItem("user");
  if (storedUser) {
    try {
      const user = JSON.parse(storedUser);
      return user.accessToken || null;
    } catch {
      return null;
    }
  }
  return null;
};

export interface MediaStreamOptions {
  contentType: MediaContentType;
  stageFilter?: MediaStage[];
  excludePostStatuses?: PostStatus[];
  offset?: number;
  limit?: number;
  onlyNonLeadCarousel?: boolean;
  websiteTags?: string[];
}

export interface MediaStreamCallbacks {
  onMediaItem?: (item: MediaItem) => void;
  onTotal?: (
    total:
      | number
      | {
          count: number;
          hasMore?: boolean;
          offset?: number;
          pageCount?: number;
        },
  ) => void;
  onComplete?: (stats?: { totalProcessed: number; totalFound: number }) => void;
  onError?: (error: string) => void;
}

export const mediaService = {
  /**
   * Stream MediaItems for a single content type from the backend.
   * Always uses /client/<uuid>/media/stream. The legacy /media endpoint
   * (which hardcoded stages=draft,approved) was removed — a missing
   * stageFilter now correctly means "all stages".
   */
  streamMediaItems(
    clientId: string,
    options: MediaStreamOptions,
    callbacks: MediaStreamCallbacks = {},
  ): Promise<MediaItem[]> {
    return new Promise((resolve, reject) => {
      const items: MediaItem[] = [];
      let streamCompletedNormally = false;

      const accessToken = getAccessToken();
      if (!accessToken) {
        reject(new Error("No access token available. Please log in."));
        return;
      }

      const url = new URL(`${API_URL}/client/${clientId}/media/stream`);
      url.searchParams.append("access_token", accessToken);

      if (options.stageFilter) {
        url.searchParams.append("stage_filter", options.stageFilter.join(","));
      }
      if (options.excludePostStatuses) {
        url.searchParams.append(
          "exclude_post_statuses",
          options.excludePostStatuses.join(","),
        );
      }
      if (options.onlyNonLeadCarousel) {
        url.searchParams.append("only_non_lead_carousel", "true");
      }
      if (options.websiteTags && options.websiteTags.length > 0) {
        url.searchParams.append("website_tags", options.websiteTags.join(","));
      }
      url.searchParams.append("content_type", options.contentType);

      if (options.offset !== undefined) {
        url.searchParams.append("offset", String(options.offset));
      }
      if (options.limit !== undefined) {
        url.searchParams.append("limit", String(options.limit));
      }

      const eventSource = new EventSource(url.toString());

      eventSource.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);

          switch (data.type) {
            case "total":
              if (callbacks.onTotal) {
                if (data.hasMore !== undefined || data.offset !== undefined) {
                  callbacks.onTotal(data);
                } else {
                  callbacks.onTotal(data.count);
                }
              }
              break;

            case "total_update":
            case "progress_update":
              if (callbacks.onTotal) {
                callbacks.onTotal(data.count || data.totalFound || data.total);
              }
              break;

            case "media_item":
              try {
                const mediaItem = MediaItem.fromBackend(
                  data.data,
                  UserRole.ADMIN,
                  clientId,
                  accessToken,
                );

                const existingIndex = items.findIndex(
                  (img) => img.id === mediaItem.id,
                );
                if (existingIndex >= 0) {
                  items[existingIndex] = mediaItem;
                } else {
                  items.push(mediaItem);
                }

                if (callbacks.onMediaItem) {
                  callbacks.onMediaItem(mediaItem);
                }
              } catch (itemError) {
                if (callbacks.onError) {
                  callbacks.onError(
                    `Failed to process media item: ${data.data?.originalFilename || "unknown"}`,
                  );
                }
              }
              break;

            case "error":
              if (callbacks.onError) {
                callbacks.onError(
                  data.error || data.message || "Unknown error",
                );
              }
              break;

            case "warning":
              if (callbacks.onError) {
                callbacks.onError(data.message || "Warning occurred");
              }
              break;

            case "item_healing":
              // Backend is auto-healing a missing file
              // The healed item will come through as a regular media_item event
              break;

            case "item_failed":
              if (callbacks.onError) {
                callbacks.onError(
                  `Failed to process item ${data.mediaId}: ${data.error}`,
                );
              }
              break;

            case "finished":
              streamCompletedNormally = true;
              eventSource.close();

              // Items are already ordered by backend (newest to oldest via created_at DESC)
              // No need to re-sort by position

              if (callbacks.onComplete) {
                callbacks.onComplete({
                  totalProcessed: data.totalProcessed || items.length,
                  totalFound:
                    data.totalFound || data.totalProcessed || items.length,
                });
              }
              resolve(items);
              break;

            case "complete":
            case "completion_summary":
              // Handle completion events from SSE keepalive wrapper
              if (data.reason === "timeout") {
                if (callbacks.onError) {
                  callbacks.onError("Server stream timeout");
                }
              }
              break;

            case "timeout":
              // Handle timeout events from SSE keepalive wrapper
              if (callbacks.onError) {
                callbacks.onError(data.message || "Server timeout");
              }
              break;

            default:
              break;
          }
        } catch (error) {
          if (callbacks.onError) {
            callbacks.onError(
              "Error parsing server response - continuing stream",
            );
          }
        }
      };

      eventSource.onerror = (error) => {
        eventSource.close();

        if (streamCompletedNormally) {
          return;
        }

        // Items are already ordered by backend (newest to oldest via created_at DESC)

        if (callbacks.onError) {
          callbacks.onError("Connection interrupted - showing loaded media");
        }

        if (callbacks.onComplete) {
          callbacks.onComplete({
            totalProcessed: items.length,
            totalFound: items.length,
          });
        }

        resolve(items);
      };

      const timeoutDuration = 1800000; // 30 minutes
      const timeoutId = setTimeout(() => {
        if (eventSource.readyState !== EventSource.CLOSED) {
          eventSource.close();

          // Items are already ordered by backend (newest to oldest via created_at DESC)

          if (callbacks.onError) {
            callbacks.onError(
              `Request timeout after ${timeoutDuration / 1000} seconds - showing loaded media`,
            );
          }

          if (callbacks.onComplete) {
            callbacks.onComplete({
              totalProcessed: items.length,
              totalFound: items.length,
            });
          }

          resolve(items);
        }
      }, timeoutDuration);

      eventSource.addEventListener("close", () => {
        clearTimeout(timeoutId);
      });
    });
  },
};
